'use client';

import { CloudPayUserSummaryResponse } from '@stadionhq-v3/stream-api';
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';

/**
 * TODO: Move this over to the @stadionhq-v3/stream-ui package
 * @typedef StreamSessionProviderProps
 * @description Props for the `StreamSessionProvider` component.
 * @property {ReactNode} children - The children components that will have access to the context.
 * @property {CloudPayUserSummaryResponse | null} [account] - The user's account information.
 * @property {boolean} [activeSession] - Indicates whether there is an active session.
 * @property {string | null} [ssoJwt] - The SSO JWT token for authentication.
 */
export type StreamSessionProviderProps = {
  children: ReactNode;
  account?: CloudPayUserSummaryResponse | null;
  activeSession?: boolean;
  ssoJwt?: string | null;
};

/**
 * @typedef StreamSessionContextProps
 * @description The context value provided by `StreamSessionProvider`.
 * @property {CloudPayUserSummaryResponse | null} account - The user's account information.
 * @property {React.Dispatch<React.SetStateAction<CloudPayUserSummaryResponse | null>>} setAccount - Setter for updating the account information.
 * @property {string | null} ssoJwt - The SSO JWT token for authentication.
 * @property {React.Dispatch<React.SetStateAction<string | null>>} setSsoJwt - Setter for updating the SSO JWT token.
 * @property {boolean} activeSession - Indicates whether there is an active session.
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setActiveSession - Setter for updating the active session state.
 */
export type StreamSessionContextProps =
  | {
      account: CloudPayUserSummaryResponse | null;
      setAccount: React.Dispatch<
        React.SetStateAction<CloudPayUserSummaryResponse | null>
      >;
      ssoJwt: string | null;
      setSsoJwt: React.Dispatch<React.SetStateAction<string | null>>;
      activeSession: boolean;
      setActiveSession: React.Dispatch<React.SetStateAction<boolean>>;
    }
  | undefined;

/**
 * @constant StreamSessionContext
 * @description The context object that provides access to the stream session information throughout the component tree.
 */
export const StreamSessionContext =
  createContext<StreamSessionContextProps>(undefined);

/**
 * @function StreamSessionProvider
 * @description A context provider component that manages and provides stream session-related state
 * (account, SSO JWT token, active session) to its child components.
 *
 * @param {StreamSessionProviderProps} props - The props for the provider.
 * @returns {JSX.Element} - The context provider component that wraps its children with the `StreamSessionContext`.
 */
export function StreamSessionProvider({
  children,
  account: accountProp = null,
  ssoJwt: ssoJwtProp = null,
  activeSession: activeSessionProp = false,
}: StreamSessionProviderProps) {
  // State for managing the account information
  const [account, setAccount] = useState<CloudPayUserSummaryResponse | null>(
    accountProp
  );

  // State for managing the SSO JWT token
  const [ssoJwt, setSsoJwt] = useState<string | null>(ssoJwtProp);

  // State for managing the active session status
  const [activeSession, setActiveSession] =
    useState<boolean>(activeSessionProp);

  /**
   * @constant context
   * @description Memoized context value to ensure the context is only updated when necessary.
   */
  const context: StreamSessionContextProps = useMemo(
    () => ({
      account,
      setAccount,
      ssoJwt,
      setSsoJwt,
      activeSession,
      setActiveSession,
    }),
    [account, ssoJwt, activeSession]
  );

  /**
   * @function useEffect
   * @description Syncs the internal state with the props whenever the props change.
   */
  useEffect(() => {
    setAccount(accountProp);
    setSsoJwt(ssoJwtProp);
    setActiveSession(activeSessionProp);
  }, [accountProp, ssoJwtProp, activeSessionProp]);

  return (
    <StreamSessionContext.Provider value={context}>
      {children}
    </StreamSessionContext.Provider>
  );
}
